import { useState, useRef, useEffect, useCallback } from 'react';
import './AudioPlayer.css';

const PLAYBACK_SPEEDS = [0.75, 1, 1.25, 1.5, 2];

/**
 * AudioPlayer - Inline audio playback controls for TTS responses.
 * Renders a play/pause button, progress bar, time display, speed selector, and replay.
 *
 * Props:
 *   audioBase64 - base64-encoded audio data
 *   mimeType    - MIME type (default 'audio/mpeg')
 *   playToken   - bump this counter to play from the start (the speaker button
 *                 increments it on every click, so the first click autoplays and
 *                 later clicks replay)
 *   isActive    - false pauses this player; the parent keeps exactly one active
 *   onPlay      - called when playback starts from inside the player, so the
 *                 parent can make this the active one
 */
const AudioPlayer = ({
  audioBase64,
  mimeType = 'audio/mpeg',
  playToken = 0,
  isActive = true,
  onPlay,
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [audioUrl, setAudioUrl] = useState(null);

  useEffect(() => {
    if (!audioBase64) return;
    try {
      const audioData = atob(audioBase64);
      const audioBytes = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        audioBytes[i] = audioData.charCodeAt(i);
      }
      const blob = new Blob([audioBytes], { type: mimeType });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    } catch (err) {
      console.error('AudioPlayer: failed to decode audio', err);
    }
  }, [audioBase64, mimeType]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => setDuration(audio.duration);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
  }, [audioUrl]);

  // Play from the start whenever the parent bumps playToken — the speaker click
  // for this message. Waits for audioUrl, so the very first click plays as soon
  // as the synthesized audio arrives.
  useEffect(() => {
    const audio = audioRef.current;
    // isActive guards the case where audio for an earlier request lands after
    // the user has already moved on to another message — without it this player
    // would start, then immediately be paused by the effect below.
    if (!audio || !audioUrl || !playToken || !isActive) return;
    audio.currentTime = 0;
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch((err) => console.error('AudioPlayer: play failed', err));
  }, [audioUrl, playToken, isActive]);

  // Only one player may sound at a time; the parent flips isActive off on the
  // others when a new message starts playing.
  useEffect(() => {
    if (isActive) return;
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
    }
    setIsPlaying(false);
  }, [isActive]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      onPlay?.();
      audio.play().catch((err) => console.error('AudioPlayer: play failed', err));
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, onPlay]);

  const handleSeek = useCallback((e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
  }, [duration]);

  const handleReplay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    onPlay?.();
    audio.currentTime = 0;
    audio.play().catch((err) => console.error('AudioPlayer: replay failed', err));
    setIsPlaying(true);
  }, [onPlay]);

  const cycleSpeed = useCallback(() => {
    const idx = PLAYBACK_SPEEDS.indexOf(speed);
    const nextSpeed = PLAYBACK_SPEEDS[(idx + 1) % PLAYBACK_SPEEDS.length];
    setSpeed(nextSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextSpeed;
    }
  }, [speed]);

  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (!audioUrl) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="audio-player">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      <button
        type="button"
        className="audio-player__btn audio-player__play"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <rect x="3" y="2" width="4" height="12" rx="1" />
            <rect x="9" y="2" width="4" height="12" rx="1" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 2.5v11l9-5.5L4 2.5z" />
          </svg>
        )}
      </button>

      <div className="audio-player__progress" onClick={handleSeek}>
        <div className="audio-player__track">
          <div
            className="audio-player__fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <span className="audio-player__time">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>

      <button
        type="button"
        className="audio-player__btn audio-player__speed"
        onClick={cycleSpeed}
        aria-label={`Playback speed ${speed}x`}
      >
        {speed}x
      </button>

      <button
        type="button"
        className="audio-player__btn audio-player__replay"
        onClick={handleReplay}
        aria-label="Replay"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 3V1L4 4l4 3V5c2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5H1c0 3.87 3.13 7 7 7s7-3.13 7-7-3.13-7-7-7z" />
        </svg>
      </button>
    </div>
  );
};

export default AudioPlayer;
